import * as React from 'react';
import {useCallback, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, useEffect} from 'react';
import {
  InteractionManager,
  Platform,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import Animated, {Clock} from 'react-native-reanimated';
import {images} from '../../../../../../assets/icons';
import {useStyles} from '../../../../../hooks/styles';
import {useTheme} from '../../../../../theme';
import {runTiming} from '../../../../../utils/runTiming';
import {Text} from '../../../../text';
import {BaseFieldText} from '../../text';
import {BaseFieldSelectAndroidItemProps, BaseFieldSelectProps, SelectPropsAndroid} from '../model';
import {Loader} from '../../../../loader';
import {useIsSmallScreenDevice} from '../../../../../hooks/useIsSmallScreenDevice';

export const BaseFieldSelectDinamic: React.FC<BaseFieldSelectProps> = (props) => {
  const {input, value, values, isSearchEnabled, setAddress} = props;
  const pickerRef = useRef(null);
  const inputContainerRef = useRef<View>(null);
  const {input: inputStyles} = useTheme();

  const handleChange = useCallback(
    (newValue: string | number) => {
      if (value !== newValue && setAddress) {
        setAddress((prev) => {
          const newVal = newValue.toString();
          return newVal;
        });
      }
    },
    [value, setAddress],
  );

  const handleToggle = useCallback(() => {
    // @ts-ignore
    pickerRef.current && pickerRef.current.togglePicker();
  }, [pickerRef]);

  const [isChevronOpen, setIsChevronOpen] = useState(false);

  const handleChevronState = useCallback(() => {
    setIsChevronOpen(!isChevronOpen);
  }, [isChevronOpen]);

  const [clock] = useState(new Clock());
  // @ts-ignore
  const IconStyle: Animated.AnimateStyle<ViewStyle> = useMemo(
    () => ({
      transform: [{rotate: runTiming(clock, isChevronOpen ? 0 : Math.PI, isChevronOpen ? Math.PI : 0)}],
    }),
    [isChevronOpen],
  );

  const inputProps = useMemo(
    () => ({
      ...input,
      value: values?.find((item) => item.value === input.value?.toString())?.label ?? input.value,
    }),
    [input],
  );

  return (
    <>
      <TouchableWithoutFeedback style={serviceStyles.fullWidth}>
        <View
          pointerEvents={isSearchEnabled ? 'auto' : 'box-only'}
          ref={inputContainerRef}
          style={serviceStyles.fullWidth}>
          <BaseFieldText
            {...props}
            input={{
              ...inputProps,
              onChange: (val) => {
                if (isSearchEnabled && isChevronOpen) {
                  handleChange(Platform.OS === 'web' ? val?.target?.value : val);
                  handleToggle();
                }
              },
              onFocus: () => handleChevronState(),
              onBlur: () => handleChevronState(),
            }}
            right={
              <images.ChevronUpDropdown
                width={24}
                height={24}
                color={isChevronOpen ? inputStyles.common.tintColor : inputStyles.common.baseColor}
              />
            }
            rightStyle={IconStyle}
            autoFocus={false}
            baseColor={isChevronOpen ? inputStyles.common.tintColor : undefined}
          />
        </View>
      </TouchableWithoutFeedback>
      <BaseFieldSelectAndroid
        // @ts-ignore ToDo
        onChange={setAddress}
        values={values || []}
        value={(value as string) || input?.value}
        placeholder={props.iosPlaceholderItem}
        ref={pickerRef}
        inputContainerRef={inputContainerRef}
      />
    </>
  );
};

type TogglePicker = {togglePicker: () => void};

const _BaseFieldSelectAndroid = React.forwardRef<TogglePicker, SelectPropsAndroid>((props, ref) => {
  return <InternalBaseFieldSelectAndroid {...props} forwardRef={ref} />;
});

const BaseFieldSelectAndroid = React.memo(_BaseFieldSelectAndroid);

const InternalBaseFieldSelectAndroid: React.FC<SelectPropsAndroid> = ({
  values,
  value,
  onChange,
  forwardRef,
  inputContainerRef,
}) => {
  const isSmallScreen = useIsSmallScreenDevice();
  const styles = useStyles(({theme}) => ({
    wrap: {
      ...theme.modal.wrap,
      ...theme.input.select.wrap,
    },
  }));

  const [isListOpen, setIsListOpen] = useState(false);

  const togglePicker = useCallback(() => {
    setIsListOpen(!isListOpen);
  }, [isListOpen]);

  const handleChange = useCallback(
    (newValue: string | number) => {
      onChange(newValue as string);
      togglePicker();
    },
    [onChange, togglePicker],
  );

  useEffect(() => {
    if (values?.length !== 0) setIsListOpen(true);
  }, [values]);

  const [layout, setLayout] = useState({width: 300, height: 0, offsetX: 0, offsetY: 0, ready: false});
  useLayoutEffect(() => {
    if (!isListOpen) return;
    InteractionManager.runAfterInteractions(() => {
      inputContainerRef?.current?.measure((x, y, width, height) => {
        setLayout({
          width,
          height,
          offsetX: 0,
          offsetY: 0,
          ready: true,
        });
      });
    });
  }, [isListOpen]);

  useImperativeHandle(
    forwardRef,
    () => ({
      togglePicker,
    }),
    [togglePicker],
  );

  if (values && values?.length === 0 && value !== '')
    return (
      <View style={{alignSelf: 'center', justifyContent: 'center', marginTop: isSmallScreen ? 0 : 50}}>
        <Loader />
      </View>
    );

  // Высота в 56 - это высота инпута в 1 строку. Не получается не хардкодить, потому что рефа на саму обертку инпута без хинта снизу - нет
  const hardOffset = 55.5;
  return (
    <Animated.ScrollView
      style={[
        {
          position: Platform.OS === 'web' ? 'relative' : 'absolute',
          top: Platform.OS === 'web' ? layout.offsetY : layout.offsetY + hardOffset,
          left: layout.offsetX,
          width: '100%',
          opacity: 100,
          maxHeight: isSmallScreen ? '25%' : '50%',
        },
        styles.wrap,
      ]}>
      {isListOpen &&
        values.map((v, idx) => (
          <BaseFieldSelectAndroidItem
            id={v.id}
            key={v.value}
            label={v.value}
            value={v.value}
            onSelect={handleChange}
            selected={v.value === value}
            isLastItem={idx === values.length - 1}
            itemNumber={idx}
          />
        ))}
    </Animated.ScrollView>
  );
};

const _BaseFieldSelectAndroidItem: React.FC<BaseFieldSelectAndroidItemProps> = ({
  id,
  label,
  value,
  selected,
  onSelect,
  isLastItem,
  itemNumber,
}) => {
  const styles = useStyles(({theme}) => ({
    item: {
      paddingVertical: 12,
      paddingHorizontal: 16,
      ...(isLastItem ? {} : theme.hr),
    },
    itemText: {
      ...theme.fonts.body,
      color: theme.color.textColor,
    },
    activeItem: {
      backgroundColor: theme.color.primaryColor,
    },
    activeItemText: {
      color: theme.color.whiteTextColor,
    },
  }));
  const handleSelect = () => onSelect(value);

  return (
    <TouchableOpacity
      testID={`andoird-select-item-${itemNumber || id}-id`}
      onPress={handleSelect}
      style={[styles.item, selected ? styles.activeItem : {}]}>
      <Text style={[styles.itemText, selected ? styles.activeItemText : {}]}>{label}</Text>
    </TouchableOpacity>
  );
};

const BaseFieldSelectAndroidItem = React.memo(_BaseFieldSelectAndroidItem);

const serviceStyles = StyleSheet.create({
  fullWindow: {
    width: '100%',
    height: '100%',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  },
  fullWidth: {width: '100%'},
});
